import { Box, Button, Typography } from "@mui/material";
import TitleSvg from "../../assets/svg/title.svg";
import LightBulbs from "../../assets/svg/lightBulbs.svg";
import SunBurst from "../../assets/svg/sunBurst.svg";
import { useEffect, useState } from "react";
import { Tune, Fullscreen, FullscreenExit } from "@mui/icons-material";
import Confetti from "react-confetti";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import spinSound from "../../assets/audio/rnp.mp3";
import { Helmet, HelmetProvider } from "react-helmet-async";

function App() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [chosenName, setChosenName] = useState("");
  const [winner, setWinner] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [data, setData] = useState();

  const navigate = useNavigate();

  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        alert(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
        );
      });
    } else {
      document.exitFullscreen();
    }

    if (isFullScreen) {
      setIsFullScreen(false);
    } else {
      setIsFullScreen(true);
    }
  };

  const audio = new Audio(spinSound);

  const playSound = () => {
    audio.play();
  };

  const totalTime = 4000;
  const intervalTime = 350;
  const totalIterations = Math.ceil(totalTime / intervalTime);

  const pickRandomName = () => {
    if (
      localStorage
        .getItem("names")
        ?.split("\n")
        .map((name) => name.trim()).length < 2
    ) {
      navigate("settings");
    } else {
      setChosenName("");
      setWinner(false);
      setDrawing(true);
      let currentIteration = 0;
      let currentIndex = 0;
      let shuffledNames = localStorage
        .getItem("names")
        .split("\n")
        .map((name) => name.trim())
        .sort(() => Math.random() - 0.5);

      const interval = setInterval(() => {
        setChosenName(shuffledNames[currentIndex]);
        if (JSON.parse(localStorage.getItem("soundEffect")) === true) {
          playSound();
        }
        currentIteration++;

        if (currentIteration >= totalIterations) {
          clearInterval(interval);
          setWinner(true);
          setDrawing(false);

          if (JSON.parse(localStorage.getItem("removeWinner")) === true) {
            const chosenName = shuffledNames[currentIndex];
            const newNames = shuffledNames.filter(
              (name) => name !== chosenName
            );
            localStorage.setItem("names", newNames.join("\n"));
          }
        }

        currentIndex = (currentIndex + 1) % shuffledNames.length;
      }, intervalTime);
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/homepage`
      );
      setData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="icon" href={data?.homePage?.[0]?.image} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <meta name="description" content="RNP | Home" />
        <link rel="apple-touch-icon" href={data?.homePage?.[0]?.image} />
        <title>RNP | Home</title>
      </Helmet>
      <Box
        sx={{
          background: data?.homePage?.[0]?.color,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "1em",
            right: "1em",
            display: "flex",
            gap: "1em",
            zIndex: "9",
          }}
        >
          <Button
            sx={{
              p: 0,
              m: 0,
              width: "fit-content",
              minWidth: "fit-content",
              color: "#FFF",
            }}
            onClick={handleFullScreen}
          >
            {isFullScreen ? (
              <FullscreenExit sx={{ width: "30px", height: "30px" }} />
            ) : (
              <Fullscreen sx={{ width: "30px", height: "30px" }} />
            )}
          </Button>
          <Button
            sx={{
              p: 0,
              m: 0,
              width: "fit-content",
              minWidth: "fit-content",
              color: "#FFF",
            }}
            onClick={() => navigate("settings")}
            disabled={drawing}
          >
            <Tune sx={{ width: "30px", height: "30px" }} />
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "4em",
            position: "relative",
            width: "100%",
            p: "2em",
            height: "100vh",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "start", width: "100%" }}>
            <img
              src={data?.homePage?.[0]?.image}
              alt=""
              style={{
                maxWidth: "150px",
                maxHeight: "100px",
                minWidth: "100px",
                minHeight: "50px",
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "4em",
              position: "relative",
            }}
          >
            <Box sx={{ width: "20vw", minWidth: "300px" }}>
              <img
                src={TitleSvg}
                alt=""
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
            {winner && (
              <>
                <Confetti
                  style={{
                    borderRadius: "50%",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "40%",
                  }}
                >
                  <img
                    src={SunBurst}
                    className="sunBurstImage"
                    alt="SunBurst"
                    style={{ width: "100%", height: "100%" }}
                  />
                </Box>
              </>
            )}

            <Box
              sx={{
                background: `#ffbf1f url(${LightBulbs})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                boxShadow: ".625rem .625rem 0 rgba(0,0,0,.2)",
                height: "180px",
                margin: "0 auto",
                padding: "1.625rem",
                width: "780px",
                zIndex: 3,
                borderRadius: "1.25rem",
                position: "relative",
                transform: {
                  xs: "scale(0.4)",
                  sm: "scale(0.55)",
                  md: "scale(0.8)",
                  lg: "scale(0.9)",
                  xl: "scale(1)",
                },
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  borderRadius: "1.25rem",
                  position: "absolute",
                  bgcolor: "#fff",
                  overflow: "hidden",
                  top: "1.5em",
                  right: "1.5em",
                  bottom: "1.5em",
                  left: "1.5em",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {chosenName && (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      animation: winner
                        ? null
                        : `spin-wheel 0.35s linear ${totalTime}`,
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {chosenName}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              sx={{
                bgcolor: "#ffbf1f",
                fontWeight: 600,
                color: "#FFF",
                boxShadow: ".625rem .625rem 0 rgba(0,0,0,.2)",
                width: "200px",
                "&:hover": {
                  bgcolor: "#e6a616",
                },
              }}
              onClick={pickRandomName}
              disabled={drawing}
            >
              Draw
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: "#FFF" }}>
            Powered by ShutterStudio
          </Typography>
        </Box>
      </Box>
    </HelmetProvider>
  );
}

export default App;
